import React, { useState, useEffect, KeyboardEvent } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ModalFooter,
  Text,
  Textarea,
  Image,
} from '@chakra-ui/react';
import AleaCheckIcon from '../../icons/AleaCheckIcon.tsx';
import AccountService from '../../services/AccountService.ts';
import { useAuth } from '../../context/AuthContext.tsx';
import { useManifest } from '../../context/ManifestContext.tsx';
import { ICreateAccountPayload } from '../../interfaces/Account/ICreateAccountPayload.ts';
import { useTranslation } from 'react-i18next';
import AleaSelectedTag from '../../components/AleaSelectedTag/AleaSelectedTag.tsx';
import enterIcon from '/assets/images/icons/enter.svg';
import AleaLoader from '../../components/AleaLoader/AleaLoader.tsx';
import { generateLangForPayload } from '../../utils/generateLangForPayload.ts';

interface InviteNewUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm?: (email: string, message?: string) => void;
  role?: string;
  carehomeId?: string | null;
}

const InviteNewUserModal: React.FC<InviteNewUserModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  role = 'Care home management',
  carehomeId,
}) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { languages: manifestLanguages } = useManifest();
  const [emailInput, setEmailInput] = useState('');
  const [emails, setEmails] = useState<string[]>([]);
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const isEmailValid = /^\S+@\S+\.\S+$/.test(emailInput);
  const canAddEmail =
    isEmailValid && !emails.includes(emailInput.trim());
  const activeLanguageCode = generateLangForPayload(
    i18n.resolvedLanguage || i18n.language,
    manifestLanguages
  );

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setEmailInput('');
      setEmails([]);
      setMessage('');
      setIsSubmitting(false);
      setHasAttemptedSubmit(false);
    }
  }, [isOpen]);

  // Ensure header/backdrop z-index behaves correctly while modal is open
  useEffect(() => {
    try {
      const ev = new CustomEvent('alea-global-modal-open', { detail: isOpen });
      window.dispatchEvent(ev);
    } catch {}
    return () => {
      try {
        const ev = new CustomEvent('alea-global-modal-open', { detail: false });
        window.dispatchEvent(ev);
      } catch {}
    };
  }, [isOpen]);

  const handleAddEmail = () => {
    setHasAttemptedSubmit(true);
    if (!canAddEmail) {
      return;
    }
    setEmails((prev) => [...prev, emailInput.trim()]);
    setEmailInput('');
  };

  const handleEmailKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddEmail();
    }
  };

  const handleEmailBlur = () => {
    // If user leaves the field with a valid, new email, add it as a tag automatically
    if (canAddEmail) {
      setEmails((prev) => [...prev, emailInput.trim()]);
      setEmailInput('');
    }
  };

  const handleRemoveEmail = (email: string) => {
    setEmails((prev) => prev.filter((e) => e !== email));
  };

  const handleSubmit = async () => {
    setHasAttemptedSubmit(true);
    if (emails.length === 0) {
      return;
    }

    try {
      setIsSubmitting(true);
      for (const email of emails) {
        const payload: ICreateAccountPayload = {
          email,
          username: email,
          first_name: '-',
          middle_name: '',
          last_name: '-',
          position: '',
          phone_number: '',
          role: role,
          is_active: true,
          message,
          organisation_id: user?.organisation?.id || '',
          carehome_id:
            carehomeId !== undefined ? carehomeId : user?.carehome?.id || '',
          lang: activeLanguageCode,
          password: '',
        };
        // Send invite for each email
        await AccountService.createAccount(payload);
      }
      // Stop loader as soon as all requests are done
      setIsSubmitting(false);
      // Use last email for callback compatibility
      onConfirm?.(emails[emails.length - 1], message);
      onClose();
    } finally {
      // Safety net in case of early errors
      setIsSubmitting(false);
    }
  };

  return (
    <>
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="xl">
      <ModalOverlay zIndex={1500} />
      <ModalContent
        data-testid="invite-user-modal"
        zIndex={1501}
        maxWidth="650px"
        p="0"
        bg="#F3F2F2"
        borderRadius="20px"
      >
        <ModalHeader
          textAlign="left"
          color="#000"
          fontSize="25px"
          fontWeight={500}
          pt="50px"
          pb="20px"
          px="50px"
        >
          <Text
            fontSize="25px"
            fontStyle="normal"
            fontWeight="500"
            lineHeight="normal"
          >
            {t('addUser')}
          </Text>
          <Text
            color="gray.600"
            fontSize="15px"
            fontStyle="normal"
            fontWeight="300"
            lineHeight="normal"
          >
            {t('addUserSubline')}
          </Text>
        </ModalHeader>
        <ModalBody pt={0} px="50px" pb="30px">
          <Box mb="25px">
            <FormControl
              isInvalid={hasAttemptedSubmit && emails.length === 0}
            >
              <FormLabel
                fontWeight={300}
                mb="3px"
                color="alea.500"
                pl="16px"
              >
                {t('emailAddress')}
              </FormLabel>
              <Flex alignItems="center">
                <Input
                  placeholder="Neuer.Nutzer@Pflegeheim.de"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  onKeyDown={handleEmailKeyDown}
                  onBlur={handleEmailBlur}
                  bg="white"
                  borderRadius="20px 0 0 20px"
                  borderRight="none"
                  border="1px solid #DCDCDC"
                  _focus={{ boxShadow: 'none', borderColor: '#DCDCDC' }}
                  _focusVisible={{ boxShadow: 'none', borderColor: '#DCDCDC' }}
                  fontSize="18px"
                  h="50px"
                  px="16px"
                />
                <Box
                  as="button"
                  onClick={handleAddEmail}
                  disabled={!canAddEmail}
                  h="50px"
                  w="60px"
                  bg={canAddEmail ? 'alea.500' : '#C6C6C6'}
                  borderRadius="0 20px 20px 0"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  _disabled={{
                    opacity: 0.5,
                    cursor: 'not-allowed',
                  }}
                >
                  <Image src={enterIcon} alt="Add email" />
                </Box>
              </Flex>
              {hasAttemptedSubmit && emails.length === 0 && (
                <Text color="red.500" fontSize="14px" mt="2" pl="16px">
                  {t('requiredField', { field: t('emailAddress') })}
                </Text>
              )}
            </FormControl>

            {emails.length > 0 && (
              <Flex mt="10px" flexWrap="wrap">
                {emails.map((email) => (
                  <AleaSelectedTag
                    key={email}
                    label={email}
                    onRemove={() => handleRemoveEmail(email)}
                  />
                ))}
              </Flex>
            )}
          </Box>

          <Box mb="10px">
            <Text
              fontWeight={300}
              marginBottom="3px"
              color="alea.500"
              paddingLeft="16px"
            >
              {t('personalMessage')}
            </Text>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t('personalMessageExample')}
              bg="white"
              borderRadius="20px"
              border="1px solid"
              borderColor="#DCDCDC"
              color="#000000"
              fontSize="18px"
              minH="110px"
              p="16px"
              _focus={{ borderColor: 'gray.500' }}
              _focusVisible={{ borderColor: 'alea.500' }}
            />
          </Box>
          <Text
            mt="50px"
            color="alea.500"
            fontSize="15px"
            fontStyle="normal"
            fontWeight={300}
            lineHeight="normal"
          >
            {t('addUserInvitationLinkInfo')}
          </Text>
        </ModalBody>
        <ModalFooter pt={0} pb="50px" px="50px">
          <Flex justifyContent="flex-end" alignItems="center" width="100%">
            <Button
              data-testid="invite-user-modal-cancel"
              variant="link"
              onClick={onClose}
              color="alea.500"
              textDecoration="underline"
              fontSize="18px"
              fontWeight={400}
            >
              {t('general.cancel')}
            </Button>
            <Button
              data-testid="invite-user-modal-submit"
              colorScheme="alea"
              variant="solid"
              onClick={handleSubmit}
              h="50px"
              w="100px"
              borderRadius="20px"
              px="0"
              display="flex"
              alignItems="center"
              justifyContent="center"
              aria-label="Einladung senden"
              ml="25px"
              isLoading={isSubmitting}
            >
              <AleaCheckIcon color="white" size={29} />
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>

    {isSubmitting && <AleaLoader />}
    </>
  );
};

export default InviteNewUserModal;


